import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { clsx } from 'clsx';
import { format } from 'date-fns';
import {
  User,
  Clock,
  Tag,
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  X,
  Paperclip,
  FileText,
  ClipboardList,
  Loader2,
  Search,
  Bot,
  FolderTree,
  Sparkles,
  ExternalLink,
  Check,
  UserPlus,
  Lock,
} from 'lucide-react';
import { Avatar, Button, cn } from '@centraoneplus/ui';
import { StatusBadge } from './StatusBadge';
import type { SlaDerivation } from './ticketFacts';
import { TicketTagInput } from './TicketTagInput';
import { PriorityBadge } from './PriorityBadge';
import { FileAttachments, type Attachment } from './FileAttachments';
import { AICollectionContext, type AITicketMetadata, type AICollectionMetadata } from './ai/AICollectionContext';
import { TicketTypeDropdown } from './TicketTypeSelector';
import type { TicketWithConversation } from '@services/ticket.service';
import type { TicketStatus, TicketPriority, TicketTypeFormSchema, FormFieldSchema } from '@centraoneplus/shared-types';
import { ticketTypesService } from '@services/ticket-types.service';
import { pipelineService } from '@services/pipeline.service';
import type { Stage, Pipeline } from '@/types/pipeline';
import { teamMembersService, type DirectoryEntry } from '@services/team-members.service';
import { agentAssistService, type TicketAnalysis } from '@services/agent-assist.service';
import { aiKeys } from '@hooks/useAISuggestions';
import { useAuthStore } from '@stores/authStore';
import { ASSIGNEE_LOCKED_HINT, canChangeAssignee } from '@/features/tickets/assignmentPermissions';
import { SentimentTrajectory } from './sentiment/SentimentTrajectory';
import VoiceCallPanel, { readVoiceCall } from './VoiceCallPanel';
import { resolveEmotion, legacyLabelToEmotion } from './sentiment/emotionConfig';
import { isSystemField } from '@utils/systemFields';
import { Link } from 'react-router-dom';
import { contactPhoneOf, hasCountryCode } from '@/lib/phone';
import { CallCustomerButton } from '@/features/voice/components/CallCustomerButton';

interface TicketSidebarProps {
  /** Shared SLA derivation — the band's countdown reads the same object. */
  sla?: SlaDerivation;
  /** Unset required fields, counted by whatever Line Items validates on. */
  missingRequired?: number;
  /** Jumps the centre column to the Line Items tab. */
  onGoToLineItems?: () => void;
  ticket: TicketWithConversation;
  onUpdateStatus: (status: TicketStatus) => Promise<void>;
  onUpdatePriority: (priority: TicketPriority) => Promise<void>;
  onUpdateStage?: (stageId: string) => Promise<void>;
  onUpdatePipeline?: (pipelineId: string) => Promise<void>;
  onAssign: (assigneeId: string | null) => Promise<void>;
  /** Secondary agents; the field is hidden when these are not passed. */
  onAddSecondaryAssignees?: (userIds: string[]) => Promise<void>;
  onRemoveSecondaryAssignee?: (userId: string) => Promise<void>;
  onAddTags: (tags: string[]) => Promise<void>;
  onRemoveTags: (tags: string[]) => Promise<void>;
  onUpdateTicketType?: (ticketTypeId: string | undefined) => Promise<void>;
  isUpdating?: boolean;
  // Attachments
  attachments?: Attachment[];
  onUploadAttachments?: (files: File[]) => Promise<void>;
  onDeleteAttachment?: (id: string) => Promise<void>;
  isUploadingAttachment?: boolean;
}

/**
 * `Ticket.source` is a Json column (`@default("{}")`), not a string: web
 * tickets carry `{}` and ingested ones `{ type: 'email', ... }`. Rendering it
 * raw put an object into JSX and crashed the sidebar, so collapse it to its
 * `type` and fall back to the channel.
 */
function sourceLabel(ticket: any): string | null {
  const raw = ticket.source;
  const value = typeof raw === 'string' ? raw : (raw && typeof raw === 'object' ? raw.type : null);
  const channel = typeof ticket.channel === 'string' ? ticket.channel : null;
  return (typeof value === 'string' && value.trim() ? value : null) || channel || null;
}

/** Safe date format — an absent or unparseable timestamp must not throw. */
function formatWhen(d: unknown, pattern: string): string | null {
  if (!d || (typeof d !== 'string' && typeof d !== 'number' && !(d instanceof Date))) return null;
  const parsed = new Date(d as string | number | Date);
  return Number.isNaN(parsed.getTime()) ? null : format(parsed, pattern);
}

/** DETAILS rows. Only what the ticket actually carries — no empty labels. */
export function detailRows(ticket: any): { label: string; value: string }[] {
  const when = (d?: unknown) => formatWhen(d, 'dd MMM · HH:mm');
  const reopened = ticket.reopenCount ?? ticket.reopenedCount;
  return [
    { label: 'Created', value: when(ticket.createdAt) },
    { label: 'Updated', value: when(ticket.updatedAt) },
    { label: 'Resolved', value: when(ticket.resolvedAt) },
    {
      label: 'Reopened',
      value: typeof reopened === 'number' && reopened > 0 ? String(reopened) : null,
    },
    { label: 'Source', value: sourceLabel(ticket) },
  ].filter((r): r is { label: string; value: string } => Boolean(r.value));
}

const statusOptions: TicketStatus[] = ['new', 'in_progress', 'pending', 'resolved', 'closed'];
const priorityOptions: TicketPriority[] = ['low', 'medium', 'high', 'urgent'];


// System/internal field exclusion (shared with the History timeline) lives in
// @utils/systemFields — see the import above.

// Fallback field labels when no ticket type schema is available
const fallbackFieldLabels: Record<string, string> = {
  customerName: 'Customer Name',
  visitorName: 'Customer Name',
  customerEmail: 'Email',
  visitorEmail: 'Email',
  contactEmail: 'Email',
  contactName: 'Contact Name',
  phoneNumber: 'Phone Number',
  phone: 'Phone Number',
  accountNumber: 'Account Number',
  meterNumber: 'Meter Number',
  companyName: 'Company',
  orderId: 'Order ID',
  issueType: 'Issue Type',
  productName: 'Product',
  handoffReason: 'Escalation Reason',
  // Billing fields
  billNumber: 'Bill Number',
  billingPeriod: 'Billing Period',
  amountDue: 'Amount Due',
  // Quotation fields
  quotationNumber: 'Quotation Number',
  serviceType: 'Service Type',
  requestedDate: 'Requested Date',
  // Contract fields
  contractNumber: 'Contract Number',
  contractType: 'Contract Type',
  startDate: 'Start Date',
  endDate: 'End Date',
};

/**
 * Tickets created from email/chat often store an HTML body in `description`.
 * Render that HTML down to readable text (preserving line breaks) instead of
 * showing raw tags. Plain-text descriptions are returned untouched.
 */
function htmlToPlainText(value?: string): string {
  if (!value) return '';
  // No markup and no comments → leave as-is.
  if (!/<[a-z!][\s\S]*>/i.test(value) && !value.includes('<!--')) return value;
  const cleaned = value
    // Drop <style>/<script> blocks entirely — their CSS/JS would otherwise leak
    // into textContent (e.g. Outlook's "p { margin-top:0 }" rules).
    .replace(/<style[\s\S]*?<\/style>/gi, '')
    .replace(/<script[\s\S]*?<\/script>/gi, '')
    // Drop HTML comments (incl. the <!-- --> wrapper around legacy style blocks).
    .replace(/<!--[\s\S]*?-->/g, '')
    // Preserve line breaks.
    .replace(/<\s*br\s*\/?\s*>/gi, '\n')
    .replace(/<\/\s*(p|div|li|tr|h[1-6])\s*>/gi, '\n');
  const tmp = document.createElement('div');
  tmp.innerHTML = cleaned;
  const text = tmp.textContent || '';
  return text
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/**
 * Checks if a string value is a URL
 */
function isUrl(value: string): boolean {
  if (typeof value !== 'string') return false;
  try {
    const url = new URL(value);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
}

/**
 * Formats a field value for display based on its type
 */
function formatFieldValue(value: any, fieldType?: string): string {
  if (value === null || value === undefined) return '';

  // Handle arrays (multiselect)
  if (Array.isArray(value)) {
    return value.join(', ');
  }

  // Handle booleans
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }

  // Handle dates
  if (fieldType === 'date' || fieldType === 'datetime') {
    try {
      const date = new Date(value);
      if (!isNaN(date.getTime())) {
        return fieldType === 'datetime'
          ? format(date, 'MMM d, yyyy h:mm a')
          : format(date, 'MMM d, yyyy');
      }
    } catch {
      // Fall through to string conversion
    }
  }

  // Handle numbers
  if (typeof value === 'number') {
    return value.toString();
  }

  // Handle objects
  if (typeof value === 'object') {
    // If it has a label property (like select options), use that
    if (value.label) return value.label;
    if (value.name) return value.name;
    return JSON.stringify(value);
  }

  return String(value).trim();
}

/**
 * Dynamic Details Display component
 * Shows ticket custom fields based on the ticket type configuration
 * Falls back to customer information if no custom fields are configured
 */
export function TicketDetailsDisplay({ ticket }: { ticket: TicketWithConversation }) {
  const [isLoading, setIsLoading] = useState(false);
  const [schemaFields, setSchemaFields] = useState<Map<string, FormFieldSchema>>(new Map());

  const metadata = (ticket.metadata as Record<string, any>) || {};
  const collectedFields = metadata.collectedFields || {};
  const customFields = (ticket.customFields as Record<string, any>) || {};
  // Get ticketTypeId from ticket.ticketType (relation) or from metadata
  const ticketTypeId = (ticket as any).ticketType?.id || (ticket as any).ticketTypeId || metadata.ticketTypeId;

  // Fetch ticket type form schema to get field definitions
  useEffect(() => {
    const fetchSchema = async () => {
      setIsLoading(true);
      try {
        let schema: TicketTypeFormSchema | null = null;

        if (ticketTypeId) {
          // Fetch schema for specific ticket type
          schema = await ticketTypesService.getFormSchema(ticketTypeId);
        } else {
          // Try to get default ticket type
          const defaultType = await ticketTypesService.getDefaultTicketType();
          if (defaultType) {
            schema = await ticketTypesService.getFormSchema(defaultType.id);
          }
        }

        if (schema) {
          // Build a map of field keys to field definitions for easy lookup
          const fieldMap = new Map<string, FormFieldSchema>();
          schema.fieldGroups.forEach(group => {
            group.fields.forEach(field => {
              fieldMap.set(field.key, field);
            });
          });
          setSchemaFields(fieldMap);
        }
      } catch (error) {
        console.error('Failed to fetch ticket type schema:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSchema();
  }, [ticketTypeId]);

  // Check for AI metadata in customFields
  const aiMetadata = customFields._aiMetadata as AICollectionMetadata | undefined;
  const aiCollectedFieldKeys = new Set(aiMetadata?.fieldsCollected || []);
  const isAIGenerated = metadata.aiGenerated === true;

  // For AI-generated tickets, only show fields that were actually collected
  // Check both _aiMetadata.fieldsCollected (keys) and metadata.collectedFields (values)
  const hasAICollectedFields = aiCollectedFieldKeys.size > 0;
  const hasMetadataCollectedFields = Object.keys(collectedFields).length > 0;

  // Build the display fields
  const displayFields: Array<{
    key: string;
    label: string;
    value: string;
    isHighlighted?: boolean;
    isAICollected?: boolean;
    sortOrder: number;
    fieldType?: string;
    isUrlValue?: boolean;
  }> = [];

  // Helper to get field label from schema or fallback
  const getFieldLabel = (key: string): string => {
    const schemaField = schemaFields.get(key);
    if (schemaField) {
      return schemaField.name;
    }
    return fallbackFieldLabels[key] || key
      .replace(/([A-Z])/g, ' $1')
      .replace(/_/g, ' ')
      .replace(/^./, str => str.toUpperCase())
      .trim();
  };

  // Helper to get field sort order from schema
  const getFieldSortOrder = (key: string): number => {
    const schemaField = schemaFields.get(key);
    return schemaField?.sortOrder ?? 999;
  };

  // Helper to get field type from schema
  const getFieldType = (key: string): string | undefined => {
    const schemaField = schemaFields.get(key);
    return schemaField?.fieldType;
  };

  // Helper to add field if it has a value
  const addField = (key: string, value: any, isHighlighted = false, forceAICollected = false) => {
    // Skip system/internal fields (uses dynamic pattern matching)
    if (isSystemField(key)) return;

    // Check if this field was AI-collected (from _aiMetadata or metadata.collectedFields)
    const isInMetadataCollectedFields = key in collectedFields;
    const isAICollected = forceAICollected || aiCollectedFieldKeys.has(key) || isInMetadataCollectedFields;

    // For AI-generated tickets: only show fields that were actually collected by AI
    // (a number someone saved on the ticket always shows)
    if (isAIGenerated && !isAICollected && !(key === 'phone' && ticket.contactPhone)) {
      return; // Skip non-AI-collected fields for AI-generated tickets
    }

    const fieldType = getFieldType(key);
    const formattedValue = formatFieldValue(value, fieldType);

    if (!formattedValue) return;

    const label = getFieldLabel(key);

    // Check if the value is a URL (for clickable link rendering)
    const isUrlValue = isUrl(formattedValue);

    // Avoid duplicates
    if (!displayFields.some(f => f.label === label)) {
      displayFields.push({
        key,
        label,
        value: formattedValue,
        isHighlighted,
        isAICollected,
        sortOrder: getFieldSortOrder(key),
        fieldType,
        isUrlValue,
      });
    }
  };

  // For AI-generated tickets with no collected fields, skip adding any fields
  // This keeps the detail section blank as requested
  // Check both _aiMetadata.fieldsCollected AND metadata.collectedFields
  if (!isAIGenerated || hasAICollectedFields || hasMetadataCollectedFields) {
    // Add handoff reason first (highlighted) - for voice/chat escalations
    if (metadata.handoffReason) {
      addField('handoffReason', metadata.handoffReason, true);
    }

    // Add customer info from metadata (voice/chat channels)
    ['customerName', 'visitorName', 'customerEmail', 'visitorEmail'].forEach(key => {
      if (metadata[key]) {
        addField(key, metadata[key]);
      }
    });

    // This panel is ALWAYS "Contact Details" — show only the customer's contact
    // info (the name is labelled "Customer Name"). Ticket custom fields belong
    // in the Line Items tab, so they are never dumped here, regardless of
    // channel or ticket type.
    const name = ticket.contactName || (ticket.customer as any)?.name;
    if (name) addField('customerName', name);

    // Always surface the contact email & phone when available — even when the
    // ticket already carries other custom fields. addField de-dupes by label,
    // so this is a no-op if the same value was already added above.
    const contactEmail = ticket.contactEmail || ticket.customer?.email;
    if (contactEmail) {
      addField('contactEmail', contactEmail);
    }
    const contactPhone = contactPhoneOf(ticket);
    if (contactPhone) {
      addField('phone', contactPhone);
    }
  }

  // Saved on the ticket itself: shown even where the block above is skipped.
  if (ticket.contactPhone) {
    addField('phone', ticket.contactPhone);
  }

  // If we have schema fields but no custom data, and schemaFields exist,
  // this means the ticket type has configured fields but no values were submitted
  // In this case, we'll show customer basic info as fallback (only for non-AI tickets)
  const hasTicketTypeFields = schemaFields.size > 0;
  const hasCustomData = displayFields.length > 0;

  // If no custom fields and no schema, show basic customer info from ticket
  // Skip this fallback for AI-generated tickets - they should only show AI-collected fields
  if (!hasCustomData && !isLoading && !isAIGenerated) {
    // Add contact info from ticket itself
    if (ticket.contactName) {
      addField('contactName', ticket.contactName);
    }
    if (ticket.contactEmail) {
      addField('contactEmail', ticket.contactEmail);
    }
    // Add customer info if available
    if (ticket.customer) {
      if (ticket.customer.name && !displayFields.some(f => f.label === 'Customer Name')) {
        addField('customerName', ticket.customer.name);
      }
      if (ticket.customer.email && !displayFields.some(f => f.label === 'Email')) {
        addField('customerEmail', ticket.customer.email);
      }
      if ((ticket.customer as any).phone) {
        addField('phone', (ticket.customer as any).phone);
      }
    }
  }

  // Sort by sort order from schema (highlighted fields come first)
  displayFields.sort((a, b) => {
    if (a.isHighlighted && !b.isHighlighted) return -1;
    if (!a.isHighlighted && b.isHighlighted) return 1;
    return a.sortOrder - b.sortOrder;
  });

  // If still no fields to display, don't render the section
  if (displayFields.length === 0 && !isLoading) {
    return null;
  }

  // Always a "Contact Details" panel — it surfaces the customer's contact info,
  // never ticket-type custom fields (those live in the Line Items tab).
  const sectionTitle = 'Contact Details';

  return (
    <CollapsibleSection title={sectionTitle} icon={ClipboardList} defaultOpen={true}>
      {isLoading ? (
        <div className="flex items-center justify-center py-4">
          <Loader2 className="h-5 w-5 animate-spin text-[var(--co-ink-4)]" />
        </div>
      ) : displayFields.length === 0 ? (
        <div className="text-sm text-[var(--co-ink-3)] py-2">
          No details available
        </div>
      ) : (
        <div className="bg-[var(--co-raised)] rounded-[12px] border border-[var(--co-line)] overflow-hidden">
          <table className="w-full text-sm table-fixed">
            <tbody className="divide-y divide-[var(--co-line-subtle)]">
              {displayFields.map((field) => (
                <tr
                  key={field.key}
                  className={cn(
                    field.isHighlighted && 'bg-[var(--co-terracotta-wash)]',
                    field.isAICollected && !field.isHighlighted && 'bg-[var(--co-brass-wash)]'
                  )}
                >
                  <td className={cn(
                    'px-3 py-2 font-medium w-2/5',
                    field.isHighlighted
                      ? 'text-[var(--co-terracotta)]'
                      : field.isAICollected
                        ? 'text-[var(--co-brass-ink)]'
                        : 'text-[var(--co-ink-3)]'
                  )}>
                    <span className="flex items-center gap-1.5">
                      {field.isAICollected && !field.isHighlighted && (
                        <span title="AI Collected">
                          <Bot className="h-3 w-3 flex-shrink-0" />
                        </span>
                      )}
                      {field.label}
                    </span>
                  </td>
                  <td className={cn(
                    'px-3 py-2 break-words w-3/5',
                    field.isHighlighted
                      ? 'text-[var(--co-terracotta)]'
                      : 'text-[var(--co-ink)]'
                  )}>
                    {field.isUrlValue ? (
                      <a
                        href={field.value}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 px-2 py-1 rounded bg-[var(--co-brass-wash)] text-[var(--co-brass-ink)] hover:bg-[var(--co-brass-wash)] hover:text-[var(--co-brass)] transition-colors text-xs font-medium"
                      >
                        <span>Open Link</span>
                        <ExternalLink className="h-3 w-3 flex-shrink-0" />
                      </a>
                    ) : (
                      field.value
                    )}
                    {field.key === 'phone' && !hasCountryCode(field.value) && (
                      <Link
                        to={`/tickets/${ticket.id}/edit`}
                        className="mt-0.5 block text-xs font-medium text-[var(--co-terracotta)] hover:underline"
                      >
                        Add country code
                      </Link>
                    )}
                    {field.key === 'phone' && <CallCustomerButton ticketId={ticket.id} phone={field.value} />}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

        </div>
      )}
    </CollapsibleSection>
  );
}

interface CollapsibleSectionProps {
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  children: React.ReactNode;
  defaultOpen?: boolean;
}

/* Shared rounded styling for the sidebar's controls. */
const FIELD_LABEL = 'mb-1.5 block text-[12px] font-medium text-[var(--co-ink-3)]';
const FIELD =
  'flex w-full items-center justify-between gap-2 rounded-[10px] border border-[var(--co-line)] bg-[var(--co-raised)] px-3 py-2 text-left transition-colors duration-[120ms] hover:border-[var(--co-brass)] disabled:opacity-60';
/**
 * The panel scrolls, so a list opened near its foot would sit below the visible
 * area. Bringing it into view on mount is what makes every option reachable.
 */
const revealMenu = (el: HTMLElement | null) => {
  el?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
};

const MENU =
  'absolute z-20 mt-1.5 max-h-60 w-full overflow-y-auto rounded-[12px] border border-[var(--co-line)] bg-[var(--co-raised)] p-1 shadow-[var(--co-shadow-pop)]';
const menuOption = (active: boolean) =>
  clsx(
    'flex w-full items-center justify-between gap-2 rounded-[8px] px-2.5 py-1.5 text-left transition-colors duration-[120ms]',
    active ? 'bg-[var(--co-brass-wash)]' : 'hover:bg-[var(--co-brass-wash)]',
  );

/** Click-away layer behind an open menu. */
function MenuBackdrop({ onClose }: { onClose: () => void }) {
  return <div className="fixed inset-0 z-10" onClick={onClose} />;
}

/**
 * Someone the assignee pickers can offer: an active user from the people
 * directory. The full team-member list is admin-only (#18946), so agents and
 * supervisors would get an empty picker from it.
 */
export type PickerAgent = Pick<DirectoryEntry, 'id' | 'email' | 'firstName' | 'lastName' | 'displayName'>;

/** Active users for the assignee pickers (readable by any signed-in user). */
export const loadPickerAgents = async (): Promise<PickerAgent[]> =>
  (await teamMembersService.getDirectory({ activeOnly: true })).data;

const teamMemberName = (a: PickerAgent) =>
  a.displayName || `${a.firstName || ''} ${a.lastName || ''}`.trim() || a.email;

/**
 * Who may add or remove secondary agents, as far as the UI can tell: admins,
 * owners, supervisors and the ticket's assignee (an agent who can no longer
 * change the assignee can still bring in help this way). ticket-service has
 * the final say and answers 403 otherwise.
 */
export function canManageSecondaryAgents(
  user: { id?: string; role?: string } | null | undefined,
  assigneeId: string | undefined,
): boolean {
  if (!user?.id) return false;
  if (['admin', 'owner', 'supervisor'].includes(user.role || '')) return true;
  return !!assigneeId && assigneeId === user.id;
}

/**
 * Agents offered in the secondary-agent picker: active members matching the
 * search, never the assignee. Ones already on the ticket stay listed (checked)
 * so a click can remove them.
 */
export function secondaryAgentChoices(
  agents: PickerAgent[],
  assigneeId: string | undefined,
  search: string,
): PickerAgent[] {
  const term = search.trim().toLowerCase();
  return agents.filter(
    (a) => a.id !== assigneeId && (!term || teamMemberName(a).toLowerCase().includes(term)),
  );
}

interface SecondaryAgentsFieldProps {
  assigneeId?: string;
  secondaryIds: string[];
  onAdd: (userIds: string[]) => Promise<void>;
  onRemove: (userId: string) => Promise<void>;
  disabled?: boolean;
}

/** Agents working the ticket alongside its assignee: chips, plus a toggle list. */
export function SecondaryAgentsField({
  assigneeId,
  secondaryIds,
  onAdd,
  onRemove,
  disabled = false,
}: SecondaryAgentsFieldProps) {
  const user = useAuthStore((s) => s.user);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const canManage = canManageSecondaryAgents(user, assigneeId);

  // Names for the chips and the picker list. Loaded once there is something to
  // show, and shared across tickets through the query cache.
  const { data: agents = [], isLoading } = useQuery({
    queryKey: ['team-members', 'directory', 'active'],
    queryFn: loadPickerAgents,
    enabled: open || secondaryIds.length > 0,
    staleTime: 5 * 60 * 1000,
  });
  const nameOf = (id: string) => {
    const agent = agents.find((a) => a.id === id);
    return agent ? teamMemberName(agent) : 'Agent';
  };

  const toggle = async (id: string) => {
    if (secondaryIds.includes(id)) await onRemove(id);
    else await onAdd([id]);
  };

  return (
    <div className="mt-3">
      <label className={FIELD_LABEL}>Secondary agents</label>
      <div className="relative">
        {secondaryIds.length > 0 && (
          <ul className="mb-1.5 flex flex-wrap gap-1.5" aria-label="Secondary agents">
            {secondaryIds.map((id) => {
              const name = nameOf(id);
              const isSelf = id === user?.id;
              return (
                <li
                  key={id}
                  className="flex items-center gap-1 rounded-full border border-[var(--co-line)] bg-[var(--co-raised)] py-0.5 pl-0.5 pr-1"
                >
                  <Avatar name={name} size="sm" />
                  <span className="max-w-[9rem] truncate text-xs font-medium text-[var(--co-ink)]">{name}</span>
                  {(canManage || isSelf) && (
                    <button
                      type="button"
                      onClick={() => onRemove(id)}
                      disabled={disabled}
                      aria-label={isSelf ? 'Leave this ticket' : `Remove ${name}`}
                      title={isSelf ? 'Leave this ticket' : `Remove ${name}`}
                      className="flex h-5 w-5 items-center justify-center rounded-full text-[var(--co-ink-4)] hover:bg-[var(--co-canvas)] hover:text-[var(--co-ink)] disabled:opacity-60"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        )}

        {canManage && (
          <Button
            variant="outline"
            size="sm"
            className="w-full !rounded-[10px]"
            disabled={disabled}
            onClick={() => setOpen(!open)}
          >
            <UserPlus className="mr-1 h-4 w-4" />
            {secondaryIds.length > 0 ? 'Add or remove' : 'Add secondary agent'}
          </Button>
        )}
        {!canManage && secondaryIds.length === 0 && (
          <p className="text-xs text-[var(--co-ink-4)]">None</p>
        )}

        {open && (
          <>
            <MenuBackdrop onClose={() => setOpen(false)} />
            <div ref={revealMenu} className={clsx(MENU, '!max-h-none !overflow-hidden !p-0')}>
              <div className="border-b border-[var(--co-line-subtle)] p-2">
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[var(--co-ink-4)]" />
                  <input
                    type="text"
                    placeholder="Search agents..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="w-full rounded-[8px] border border-[var(--co-line)] bg-[var(--co-surface)] py-1.5 pl-8 pr-2 text-sm text-[var(--co-ink)] outline-none focus:border-[var(--co-brass)]"
                    autoFocus
                  />
                </div>
              </div>
              <div className="max-h-48 overflow-y-auto p-1">
                {isLoading ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="h-4 w-4 animate-spin text-[var(--co-ink-4)]" />
                  </div>
                ) : (() => {
                  const choices = secondaryAgentChoices(agents, assigneeId, search);
                  return choices.length === 0 ? (
                    <p className="py-3 text-center text-sm text-[var(--co-ink-4)]">No agents found</p>
                  ) : (
                    choices.map((agent) => {
                      const name = teamMemberName(agent);
                      const isOn = secondaryIds.includes(agent.id);
                      return (
                        <button
                          key={agent.id}
                          type="button"
                          role="menuitemcheckbox"
                          aria-checked={isOn}
                          className={menuOption(isOn)}
                          disabled={disabled}
                          onClick={() => toggle(agent.id)}
                        >
                          <span className="flex min-w-0 items-center gap-2">
                            <Avatar name={name} size="sm" />
                            <span className="min-w-0">
                              <span className="block truncate text-sm font-medium text-[var(--co-ink)]">{name}</span>
                              <span className="block truncate text-xs text-[var(--co-ink-4)]">{agent.email}</span>
                            </span>
                          </span>
                          {isOn && <Check className="h-4 w-4 flex-shrink-0 text-[var(--co-brass-ink)]" />}
                        </button>
                      );
                    })
                  );
                })()}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function CollapsibleSection({
  title,
  icon: Icon,
  caption,
  children,
  defaultOpen = true,
}: CollapsibleSectionProps & { caption?: React.ReactNode }) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="border-b border-[var(--co-line-subtle)] py-3.5 last:border-b-0">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        className="-mx-2 flex w-[calc(100%+1rem)] items-center justify-between gap-2 rounded-[8px] px-2 py-1 text-left transition-colors duration-[120ms] hover:bg-[var(--co-canvas)]"
      >
        <span className="flex min-w-0 items-center gap-2.5">
          <span className="flex h-7 w-7 flex-none items-center justify-center rounded-[8px] bg-[var(--co-brass-wash)] text-[var(--co-brass-ink)]">
            <Icon className="h-3.5 w-3.5" />
          </span>
          <span className="truncate text-[13.5px] font-semibold text-[var(--co-ink)]">{title}</span>
          {caption && (
            <span className="truncate text-[11px] text-[var(--co-ink-4)]">{caption}</span>
          )}
        </span>
        {isOpen ? (
          <ChevronDown className="h-4 w-4 flex-none text-[var(--co-ink-4)]" />
        ) : (
          <ChevronRight className="h-4 w-4 flex-none text-[var(--co-ink-4)]" />
        )}
      </button>
      {isOpen && <div className="mt-3">{children}</div>}
    </div>
  );
}

// Sentiment display config lives in ./sentiment/emotionConfig (single source
// of truth, emotion-first). Legacy 4-label values (positive/neutral/negative/
// frustrated) resolve through `legacyLabelToEmotion` → `resolveEmotion`.

/**
 * AI Insights component - displays sentiment and estimated resolution time
 */
function normalizeTicketAnalysis(raw: any): TicketAnalysis {
  const sentimentLabel =
    typeof raw.sentiment === 'object' ? raw.sentiment.label : (raw.sentimentDetail?.label ?? raw.sentiment ?? 'neutral');
  const sentimentScore =
    typeof raw.sentiment === 'object' ? raw.sentiment.score : (raw.sentimentDetail?.score ?? 0);
  const sentimentTrend =
    typeof raw.sentiment === 'object' ? raw.sentiment.trend : (raw.sentimentDetail?.trend ?? 'stable');

  return {
    summary: raw.summary ?? raw.recommendedApproach ?? '',
    keyPoints: raw.keyPoints ?? raw.keyIssues ?? [],
    sentiment: {
      score: sentimentScore,
      label: sentimentLabel,
      trend: sentimentTrend,
    },
    priority: raw.priority ?? {
      suggested: raw.suggestedPriority ?? 'medium',
      reason: raw.recommendedApproach ?? '',
    },
    category: raw.category ?? {
      suggested: raw.suggestedCategory ?? 'General',
      confidence: 0.7,
    },
    estimatedResolutionTime: raw.estimatedResolutionTime,
    suggestedActions: raw.suggestedActions ?? [],
    // Additive emotion-first trajectory (oldest→newest). Pass through verbatim;
    // absent on un-upgraded backends → the single-sentiment pill is the fallback.
    sentimentTrajectory: raw.sentimentTrajectory,
  };
}

export function AIInsightsSection({
  ticketId,
  missingRequired = 0,
  onGoToLineItems,
}: {
  ticketId: string;
  /** Count of unset required fields, from Line Items' own validation. */
  missingRequired?: number;
  onGoToLineItems?: () => void;
}) {
  const tenantId = useAuthStore((s) => s.user?.tenantId ?? '');

  // React Query keeps the analysis cached per (tenant, ticket), so a remount
  // hits the cache instead of re-calling /agent-assist/analyze-ticket.
  const { data: analysis, isFetching: isLoading } = useQuery({
    queryKey: aiKeys.ticketAnalysis(tenantId, ticketId),
    queryFn: async (): Promise<TicketAnalysis> => {
      const raw: any = await agentAssistService.analyzeTicket(ticketId);
      return normalizeTicketAnalysis(raw);
    },
    enabled: !!ticketId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
    retry: 1,
  });

  // Don't render if no analysis data
  if (!analysis && !isLoading) {
    return null;
  }

  // Headline sentiment pill: prefer the trajectory's CURRENT emotion when present
  // (emotion-first), otherwise resolve the legacy 4-label `sentiment.label`.
  const headlineEmotion =
    analysis?.sentimentTrajectory?.current?.dominantEmotion ??
    legacyLabelToEmotion(analysis?.sentiment?.label);
  const sentimentConfig = resolveEmotion(headlineEmotion);
  const hasTrajectory = Boolean(analysis?.sentimentTrajectory?.points?.length);

  return (
    <CollapsibleSection title="AI Insights" icon={Sparkles} defaultOpen={true}>
      {isLoading ? (
        <div className="flex items-center justify-center py-4">
          <Loader2 className="h-5 w-5 animate-spin text-[var(--co-ink-4)]" />
        </div>
      ) : analysis ? (
        <div className="space-y-3">
          {/* Sentiment pill — only when there is no trajectory, which carries
              its own current-emotion pill. */}
          {!hasTrajectory && (
            <div className="flex items-center justify-between gap-2">
              <span className="text-[13px] text-[var(--co-ink-3)]">Sentiment</span>
              <span
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium',
                  sentimentConfig.bgColor,
                  sentimentConfig.color,
                )}
              >
                <span>{sentimentConfig.emoji}</span>
                <span>{sentimentConfig.label}</span>
              </span>
            </div>
          )}

          {analysis.estimatedResolutionTime && (
            <div className="flex items-center justify-between gap-2">
              <span className="text-[13px] text-[var(--co-ink-3)]">Est. Resolution</span>
              <span className="inline-flex items-center gap-1.5 text-[13px] font-medium text-[var(--co-ink)]">
                <Clock className="h-4 w-4 text-[var(--co-brass-ink)]" />
                {analysis.estimatedResolutionTime}
              </span>
            </div>
          )}

          {hasTrajectory && (
            <div className="rounded-[10px] border border-[var(--co-line-subtle)] bg-[var(--co-canvas)] p-3">
              <SentimentTrajectory trajectory={analysis.sentimentTrajectory!} />
            </div>
          )}

          {analysis.summary && (
            <p className="line-clamp-3 rounded-[10px] bg-[var(--co-canvas)] px-3 py-2.5 text-[12.5px] leading-[1.55] text-[var(--co-ink-2)] [text-wrap:pretty]">
              {analysis.summary}
            </p>
          )}

          {missingRequired > 0 && onGoToLineItems && (
            <button
              type="button"
              onClick={onGoToLineItems}
              className="flex w-full items-center justify-between gap-2 rounded-[10px] border border-[var(--co-terracotta)] bg-[var(--co-terracotta-wash)] px-3 py-2 text-[12px] font-medium text-[var(--co-terracotta)]"
            >
              <span>
                {missingRequired} required field{missingRequired === 1 ? '' : 's'} missing
              </span>
              <span aria-hidden="true">Line items →</span>
            </button>
          )}
        </div>
      ) : null}
    </CollapsibleSection>
  );
}

export function TicketSidebar({
  ticket,
  sla,
  missingRequired = 0,
  onGoToLineItems,
  onUpdateStatus,
  onUpdatePriority,
  onUpdateStage,
  onUpdatePipeline,
  onAssign,
  onAddSecondaryAssignees,
  onRemoveSecondaryAssignee,
  onAddTags,
  onRemoveTags,
  onUpdateTicketType,
  isUpdating = false,
  attachments = [],
  onUploadAttachments,
  onDeleteAttachment,
  isUploadingAttachment = false,
}: TicketSidebarProps) {
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [showStageDropdown, setShowStageDropdown] = useState(false);
  const [showPipelineDropdown, setShowPipelineDropdown] = useState(false);
  const [showPriorityDropdown, setShowPriorityDropdown] = useState(false);
  const [showAssignDropdown, setShowAssignDropdown] = useState(false);

  // Stage selector data. The ticket detail response embeds the pipeline with its
  // ordered stages, so prefer that and only hit the API when it's absent.
  const pipelineId = (ticket as any).pipelineId as string | undefined;

  // Pipeline selector data. Moving a ticket to a different pipeline relocates it
  // to that pipeline's first stage and syncs status (handled server-side).
  const { data: pipelines = [] } = useQuery({
    queryKey: ['pipelines'],
    queryFn: () => pipelineService.getPipelines(),
    enabled: !!onUpdatePipeline,
    staleTime: 5 * 60 * 1000,
  });
  const currentPipeline =
    pipelines.find((p) => p.id === pipelineId) ||
    ((ticket as any).pipeline as Pipeline | undefined);
  const currentStage = (ticket as any).stage as { id: string; name: string; color?: string } | undefined;
  const embeddedStages = (((ticket as any).pipeline?.stages as Stage[] | undefined) || []);
  const { data: fetchedStages } = useQuery({
    queryKey: ['pipeline-stages', pipelineId],
    queryFn: () => pipelineService.getStages(pipelineId as string),
    enabled: !!pipelineId && embeddedStages.length === 0 && !!onUpdateStage,
    staleTime: 5 * 60 * 1000,
  });
  const stages = (embeddedStages.length > 0 ? embeddedStages : fetchedStages || [])
    .slice()
    .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
  const [agents, setAgents] = useState<PickerAgent[]>([]);
  const [agentSearch, setAgentSearch] = useState('');
  const [loadingAgents, setLoadingAgents] = useState(false);

  const loadAgents = async () => {
    setLoadingAgents(true);
    try {
      setAgents(await loadPickerAgents());
    } catch (err) {
      console.error('Failed to load agents:', err);
    } finally {
      setLoadingAgents(false);
    }
  };

  // Agents can't clear or replace an existing assignee (admins and supervisors
  // can); they still pick up unassigned tickets and add secondary agents.
  const currentUser = useAuthStore((s) => s.user);
  const assigneeLocked = !canChangeAssignee(currentUser, ticket.assignee?.id ?? ticket.assigneeId);

  const toggleAssignDropdown = () => {
    if (assigneeLocked) return;
    if (!showAssignDropdown && agents.length === 0) loadAgents();
    setShowAssignDropdown(!showAssignDropdown);
  };

  const handleTagsChange = async (next: string[]) => {
    const current = ticket.tags || [];
    const added = next.filter((t) => !current.includes(t));
    const removed = current.filter((t) => !next.includes(t));
    if (added.length > 0) await onAddTags(added);
    if (removed.length > 0) await onRemoveTags(removed);
  };

  // While the SLA is paused its deadline is frozen (it moves out on resume), so
  // a deadline in the past is not a breach.
  const isSlaPaused =
    sla?.firstResponse?.state === 'paused' || sla?.resolution?.state === 'paused';
  const isFirstResponseBreached = sla?.firstResponse?.state === 'breached';
  const isResolutionBreached =
    sla?.resolution?.state === 'breached' ||
    (!isSlaPaused &&
      Boolean(ticket.resolutionDueAt && new Date(ticket.resolutionDueAt) < new Date() && !ticket.resolvedAt));
  const isSlaBreaching = !ticket.resolvedAt && (isFirstResponseBreached || isResolutionBreached);

  const voiceCall = readVoiceCall(
    ticket.metadata as Record<string, unknown> | undefined,
    (ticket as any).source as Record<string, unknown> | undefined,
  );
  // Voice tickets' description is a machine-built markdown blob of exactly the
  // facts VoiceCallPanel shows, so it is suppressed for them.
  const description = voiceCall ? '' : htmlToPlainText(ticket.description);
  const tagCount = ticket.tags?.length ?? 0;

  return (
    <div
      className="scrollbar-hide w-full flex-shrink-0 overflow-y-auto border border-[var(--co-line)] bg-[var(--co-surface)] px-4 py-1.5 lg:w-[340px]"
      style={{ borderRadius: 14, boxShadow: 'var(--co-shadow-card)' }}
    >
      {/* AI Insights - Sentiment & Estimated Resolution Time (at top) */}
      <AIInsightsSection
        ticketId={ticket.id}
        missingRequired={missingRequired}
        onGoToLineItems={onGoToLineItems}
      />

      {/* AI Collection Context - shown only for AI-generated tickets */}
      <AICollectionContext
        metadata={(ticket.metadata || {}) as AITicketMetadata}
        aiMetadata={(ticket.customFields as Record<string, any>)?._aiMetadata as AICollectionMetadata}
      />

      {/* Voice call details, for a ticket that came from a phone call. */}
      {voiceCall && <VoiceCallPanel view={voiceCall} ticketId={ticket.id} />}

      {/* Description - only shown when the ticket has a body */}
      {description && (
        <CollapsibleSection title="Description" icon={FileText} defaultOpen={false}>
          <p className="whitespace-pre-wrap break-words rounded-[10px] bg-[var(--co-canvas)] px-3 py-2.5 text-sm text-[var(--co-ink-2)]">
            {description}
          </p>
        </CollapsibleSection>
      )}

      {/* Contact Details */}
      <TicketDetailsDisplay ticket={ticket} />

      {/* Status & Priority */}
      <CollapsibleSection title="Status & Priority" icon={AlertTriangle}>
        <div className="space-y-3">
          {/* Status */}
          <div className="relative">
            <label className={FIELD_LABEL}>Status</label>
            <button
              type="button"
              onClick={() => setShowStatusDropdown(!showStatusDropdown)}
              className={FIELD}
              disabled={isUpdating}
            >
              <StatusBadge status={ticket.status} />
              <ChevronDown className="h-4 w-4 flex-shrink-0 text-[var(--co-ink-4)]" />
            </button>
            {showStatusDropdown && (
              <>
                <MenuBackdrop onClose={() => setShowStatusDropdown(false)} />
                <div ref={revealMenu} className={MENU}>
                  {statusOptions.map((status) => {
                    const isActive = status === ticket.status;
                    return (
                      <button
                        key={status}
                        type="button"
                        disabled={isUpdating}
                        onClick={() => {
                          setShowStatusDropdown(false);
                          if (!isActive) onUpdateStatus(status);
                        }}
                        className={menuOption(isActive)}
                      >
                        <StatusBadge status={status} />
                        {isActive && <Check className="h-4 w-4 flex-shrink-0 text-[var(--co-brass-ink)]" />}
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </div>

          {/* Pipeline */}
          {onUpdatePipeline && pipelines.length > 0 && (
            <div className="relative">
              <label className={FIELD_LABEL}>Pipeline</label>
              <button
                type="button"
                onClick={() => setShowPipelineDropdown(!showPipelineDropdown)}
                className={FIELD}
                disabled={isUpdating}
              >
                <span className="flex min-w-0 items-center gap-2">
                  <FolderTree className="h-4 w-4 flex-shrink-0 text-[var(--co-brass-ink)]" />
                  <span className="truncate text-sm text-[var(--co-ink)]">
                    {currentPipeline?.name || 'Select pipeline'}
                  </span>
                </span>
                <ChevronDown className="h-4 w-4 flex-shrink-0 text-[var(--co-ink-4)]" />
              </button>
              {showPipelineDropdown && (
                <>
                  <MenuBackdrop onClose={() => setShowPipelineDropdown(false)} />
                  <div ref={revealMenu} className={MENU}>
                    {pipelines.map((pipeline) => {
                      const isActive = pipeline.id === currentPipeline?.id;
                      return (
                        <button
                          key={pipeline.id}
                          type="button"
                          disabled={isUpdating}
                          onClick={() => {
                            setShowPipelineDropdown(false);
                            if (!isActive) onUpdatePipeline(pipeline.id);
                          }}
                          className={menuOption(isActive)}
                        >
                          <span className="truncate text-sm text-[var(--co-ink-2)]">{pipeline.name}</span>
                          {isActive && <Check className="h-4 w-4 flex-shrink-0 text-[var(--co-brass-ink)]" />}
                        </button>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Stage — editable when the ticket is in a pipeline; read-only
              otherwise. */}
          {(currentStage || (pipelineId && stages.length > 0)) && (
            onUpdateStage && pipelineId && stages.length > 0 ? (
              <div className="relative">
                <label className={FIELD_LABEL}>Stage</label>
                <button
                  type="button"
                  onClick={() => setShowStageDropdown(!showStageDropdown)}
                  className={FIELD}
                  disabled={isUpdating}
                >
                  <span className="flex min-w-0 items-center gap-2">
                    <span
                      className="h-2.5 w-2.5 flex-shrink-0 rounded-full"
                      style={{ backgroundColor: currentStage?.color || '#6B7280' }}
                    />
                    <span className="truncate text-sm text-[var(--co-ink)]">
                      {currentStage?.name || 'Select stage'}
                    </span>
                  </span>
                  <ChevronDown className="h-4 w-4 flex-shrink-0 text-[var(--co-ink-4)]" />
                </button>
                {showStageDropdown && (
                  <>
                    <MenuBackdrop onClose={() => setShowStageDropdown(false)} />
                    <div ref={revealMenu} className={MENU}>
                      {stages.map((stage) => {
                        const isActive = stage.id === currentStage?.id;
                        return (
                          <button
                            key={stage.id}
                            type="button"
                            disabled={isUpdating}
                            onClick={() => {
                              setShowStageDropdown(false);
                              if (!isActive) onUpdateStage(stage.id);
                            }}
                            className={menuOption(isActive)}
                          >
                            <span className="flex min-w-0 items-center gap-2">
                              <span
                                className="h-2.5 w-2.5 flex-shrink-0 rounded-full"
                                style={{ backgroundColor: stage.color || '#6B7280' }}
                              />
                              <span className="truncate text-sm text-[var(--co-ink-2)]">{stage.name}</span>
                            </span>
                            {isActive && <Check className="h-4 w-4 flex-shrink-0 text-[var(--co-brass-ink)]" />}
                          </button>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            ) : currentStage ? (
              <div>
                <label className={FIELD_LABEL}>Stage</label>
                <div className="flex items-center gap-2 rounded-[10px] border border-[var(--co-line)] bg-[var(--co-raised)] px-3 py-2">
                  <span
                    className="h-2.5 w-2.5 flex-shrink-0 rounded-full"
                    style={{ backgroundColor: currentStage.color || '#6B7280' }}
                  />
                  <span className="text-sm text-[var(--co-ink)]">{currentStage.name}</span>
                </div>
              </div>
            ) : null
          )}

          {/* Priority */}
          <div className="relative">
            <label className={FIELD_LABEL}>Priority</label>
            <button
              type="button"
              onClick={() => setShowPriorityDropdown(!showPriorityDropdown)}
              className={FIELD}
              disabled={isUpdating}
            >
              <PriorityBadge priority={ticket.priority} showLabel />
              <ChevronDown className="h-4 w-4 flex-shrink-0 text-[var(--co-ink-4)]" />
            </button>
            {showPriorityDropdown && (
              <>
                <MenuBackdrop onClose={() => setShowPriorityDropdown(false)} />
                <div ref={revealMenu} className={MENU}>
                  {priorityOptions.map((priority) => {
                    const isActive = priority === ticket.priority;
                    return (
                      <button
                        key={priority}
                        type="button"
                        disabled={isUpdating}
                        onClick={() => {
                          setShowPriorityDropdown(false);
                          if (!isActive) onUpdatePriority(priority);
                        }}
                        className={menuOption(isActive)}
                      >
                        <PriorityBadge priority={priority} showLabel />
                        {isActive && <Check className="h-4 w-4 flex-shrink-0 text-[var(--co-brass-ink)]" />}
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </div>
      </CollapsibleSection>

      {/* Classification */}
      <CollapsibleSection title="Classification" icon={FolderTree}>
        <label className={FIELD_LABEL}>Ticket Type</label>
        <TicketTypeDropdown
          className="co-sidebar-select"
          value={(ticket as any).ticketType?.id || (ticket as any).ticketTypeId || undefined}
          currentName={(ticket as any).ticketType?.name || undefined}
          onChange={(ticketTypeId) => onUpdateTicketType?.(ticketTypeId)}
          disabled={isUpdating || !onUpdateTicketType}
          placeholder="Select ticket type..."
          showLabel={false}
          autoSelect={false}
        />
      </CollapsibleSection>

      {/* Assignment — click the assignee to reassign, × to unassign (admins and
          supervisors; agents see the assignee read-only once it is set). */}
      <CollapsibleSection title="Assignment" icon={User}>
        <label className={FIELD_LABEL}>Assignee</label>
        <div className="relative">
          {ticket.assignee && assigneeLocked ? (
            <div
              className="flex items-center gap-2 rounded-[10px] border border-[var(--co-line)] bg-[var(--co-raised)] px-2.5 py-2"
              title={ASSIGNEE_LOCKED_HINT}
              data-testid="assignee-locked"
            >
              <Avatar name={ticket.assignee.name} size="sm" />
              <span className="min-w-0 flex-1 truncate text-sm font-medium text-[var(--co-ink)]">
                {ticket.assignee.name}
              </span>
              <Lock className="h-3.5 w-3.5 flex-none text-[var(--co-ink-4)]" aria-label={ASSIGNEE_LOCKED_HINT} />
            </div>
          ) : ticket.assignee ? (
            <div className="flex items-center gap-1 rounded-[10px] border border-[var(--co-line)] bg-[var(--co-raised)] p-1.5 transition-colors duration-[120ms] hover:border-[var(--co-brass)]">
              <button
                type="button"
                onClick={toggleAssignDropdown}
                disabled={isUpdating}
                title="Change assignee"
                className="flex min-w-0 flex-1 items-center gap-2 rounded-[8px] px-1 py-0.5 text-left disabled:opacity-60"
              >
                <Avatar name={ticket.assignee.name} size="sm" />
                <span className="truncate text-sm font-medium text-[var(--co-ink)]">{ticket.assignee.name}</span>
              </button>
              <button
                type="button"
                onClick={() => onAssign(null)}
                disabled={isUpdating}
                aria-label="Unassign"
                className="flex h-7 w-7 flex-none items-center justify-center rounded-full text-[var(--co-ink-4)] transition-colors duration-[120ms] hover:bg-[var(--co-canvas)] hover:text-[var(--co-ink)]"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <Button
              variant="outline"
              size="sm"
              className="w-full !rounded-[10px]"
              disabled={isUpdating}
              onClick={toggleAssignDropdown}
            >
              <User className="mr-1 h-4 w-4" />
              Assign
            </Button>
          )}

          {showAssignDropdown && (
            <>
              <MenuBackdrop onClose={() => setShowAssignDropdown(false)} />
              <div ref={revealMenu} className={clsx(MENU, '!max-h-none !overflow-hidden !p-0')}>
                <div className="border-b border-[var(--co-line-subtle)] p-2">
                  <div className="relative">
                    <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[var(--co-ink-4)]" />
                    <input
                      type="text"
                      placeholder="Search agents..."
                      value={agentSearch}
                      onChange={(e) => setAgentSearch(e.target.value)}
                      className="w-full rounded-[8px] border border-[var(--co-line)] bg-[var(--co-surface)] py-1.5 pl-8 pr-2 text-sm text-[var(--co-ink)] outline-none focus:border-[var(--co-brass)]"
                      autoFocus
                    />
                  </div>
                </div>
                <div className="max-h-48 overflow-y-auto p-1">
                  {loadingAgents ? (
                    <div className="flex items-center justify-center py-4">
                      <Loader2 className="h-4 w-4 animate-spin text-[var(--co-ink-4)]" />
                    </div>
                  ) : (() => {
                    const agentName = (a: PickerAgent) =>
                      a.displayName || `${a.firstName || ''} ${a.lastName || ''}`.trim() || a.email;
                    const filtered = agents.filter((a) =>
                      agentName(a).toLowerCase().includes(agentSearch.toLowerCase()),
                    );
                    return filtered.length === 0 ? (
                      <p className="py-3 text-center text-sm text-[var(--co-ink-4)]">No agents found</p>
                    ) : (
                      filtered.map((agent) => {
                        const name = agentName(agent);
                        const isActive = agent.id === ticket.assignee?.id;
                        return (
                          <button
                            key={agent.id}
                            type="button"
                            className={menuOption(isActive)}
                            onClick={() => {
                              setShowAssignDropdown(false);
                              setAgentSearch('');
                              if (!isActive) onAssign(agent.id);
                            }}
                          >
                            <span className="flex min-w-0 items-center gap-2">
                              <Avatar name={name} size="sm" />
                              <span className="min-w-0">
                                <span className="block truncate text-sm font-medium text-[var(--co-ink)]">{name}</span>
                                <span className="block truncate text-xs text-[var(--co-ink-4)]">{agent.email}</span>
                              </span>
                            </span>
                            {isActive && <Check className="h-4 w-4 flex-shrink-0 text-[var(--co-brass-ink)]" />}
                          </button>
                        );
                      })
                    );
                  })()}
                </div>
              </div>
            </>
          )}
        </div>
        {onAddSecondaryAssignees && onRemoveSecondaryAssignee && (
          <SecondaryAgentsField
            assigneeId={ticket.assignee?.id ?? ticket.assigneeId}
            secondaryIds={(ticket.secondaryAssignees ?? []).map((s) => s.userId)}
            onAdd={onAddSecondaryAssignees}
            onRemove={onRemoveSecondaryAssignee}
            disabled={isUpdating}
          />
        )}
      </CollapsibleSection>

      {/* Customer lives in the header card (click-through to the profile). */}

      {/* SLA */}
      {(ticket.resolutionDueAt || ticket.firstResponseDueAt) && (
        <CollapsibleSection title="SLA" icon={Clock}>
          <div className="space-y-2 rounded-[12px] bg-[var(--co-canvas)] px-3 py-2.5 text-sm">
            {ticket.firstResponseDueAt && (
              <div className="flex justify-between gap-3">
                <span className="text-[var(--co-ink-3)]">First Response</span>
                <span
                  className={clsx(
                    ticket.firstResponseAt
                      ? 'text-[var(--co-green)]'
                      : isFirstResponseBreached
                        ? 'font-medium text-[var(--co-burgundy)]'
                        : 'text-[var(--co-ink)]',
                  )}
                >
                  {ticket.firstResponseAt
                    ? 'Responded'
                    : sla?.firstResponse?.state === 'paused'
                      ? 'Paused'
                      : format(new Date(ticket.firstResponseDueAt), 'MMM d, h:mm a')}
                </span>
              </div>
            )}
            {ticket.resolutionDueAt && (
              <div className="flex justify-between gap-3">
                <span className="text-[var(--co-ink-3)]">Resolution</span>
                <span
                  className={clsx(
                    ticket.resolvedAt
                      ? 'text-[var(--co-green)]'
                      : isResolutionBreached
                        ? 'font-medium text-[var(--co-burgundy)]'
                        : 'text-[var(--co-ink)]',
                  )}
                >
                  {ticket.resolvedAt
                    ? 'Resolved'
                    : sla?.resolution?.state === 'paused'
                      ? 'Paused'
                      : format(new Date(ticket.resolutionDueAt), 'MMM d, h:mm a')}
                </span>
              </div>
            )}
            {isSlaBreaching && (
              <div className="inline-flex items-center gap-1 rounded-full border border-[var(--co-burgundy)] px-2 py-0.5 text-xs font-medium text-[var(--co-burgundy)]">
                <AlertTriangle className="h-3 w-3" />
                SLA breached
              </div>
            )}
          </div>
        </CollapsibleSection>
      )}

      {/* Tags */}
      <CollapsibleSection title="Tags" icon={Tag} caption={tagCount > 0 ? String(tagCount) : undefined}>
        <TicketTagInput
          value={ticket.tags || []}
          onChange={handleTagsChange}
          disabled={isUpdating}
          placeholder="Add tag..."
          compact
        />
      </CollapsibleSection>

      {/* Attachments — upload, preview and delete. */}
      <CollapsibleSection
        title="Attachments"
        icon={Paperclip}
        caption={attachments.length > 0 ? String(attachments.length) : undefined}
      >
        <FileAttachments
          attachments={attachments}
          onUpload={onUploadAttachments || (async () => {})}
          onDelete={onDeleteAttachment}
          isUploading={isUploadingAttachment}
          showUpload={!!onUploadAttachments}
          maxSize={50 * 1024 * 1024}
        />
      </CollapsibleSection>

      {/* Details */}
      <CollapsibleSection
        title="Details"
        icon={Clock}
        defaultOpen={false}
        caption={formatWhen(ticket.updatedAt, 'HH:mm') ? `Updated ${formatWhen(ticket.updatedAt, 'HH:mm')}` : undefined}
      >
        <dl className="rounded-[12px] bg-[var(--co-canvas)] px-3 py-2 text-[12.5px]">
          {detailRows(ticket).map((row) => (
            <div key={row.label} className="flex justify-between gap-3 py-1">
              <dt className="flex-none text-[var(--co-ink-4)]">{row.label}</dt>
              <dd className="truncate text-[var(--co-ink-2)]">{row.value}</dd>
            </div>
          ))}
        </dl>
      </CollapsibleSection>
    </div>
  );
}

export default TicketSidebar;
